/**
 * O recibo do material na Custódia da abertura (E126.F3; por envio desde a E133.F2, GUS-106,
 * ADR-0089): o caso, quando chegou, quem enviou e quantos arquivos, e cada recebimento com o ID, o
 * instante, o operador e o que trouxe, inclusive o que ficou guardado sem exame e por quê. O
 * original anterior ao registro do envio aparece num grupo sem número: o recibo não inventa envio.
 */
import type { ReactNode } from 'react';
import type { AxiosInstance } from 'axios';

import { Badge } from '@/components/ui';
import { textoDoMotivo } from '@/components/ExameEletronico/pacote_do_banco';
import { ptBR } from '@/i18n/pt-BR';
import {
  arquivosDoRecebimento,
  idCurto,
  operadoresDoRecibo,
  type NaoExaminadoNoReciboData,
  type RecebimentoNoReciboData,
  type ReciboDaCustodiaData,
} from '@/lib/abertura_do_caso';
import { formatarDataHora } from '@/lib/trilha_format';

import { ComoConferir, ManifestoDoRecebimento } from './ManifestoDoRecebimento';
import { CartaoDaAbertura } from './PecasDaAbertura';
import { SeloDaSituacao } from './SituacaoDaCustodia';

const t = ptBR.abertura.custodia;

function contagem(arquivos: number, naoExaminados: number): string {
  return naoExaminados > 0
    ? t.contagemComNaoExaminados
        .replace('{n}', String(arquivos))
        .replace('{m}', String(naoExaminados))
    : t.contagem.replace('{n}', String(arquivos));
}

/** O total do recibo: pelos envios, ou pelos originais quando nenhum envio veio no recibo. */
export function contagemDoRecibo(recibo: ReciboDaCustodiaData): string {
  if (recibo.recebimentos.length === 0) return contagem(recibo.originais.length, 0);
  const arquivos = recibo.recebimentos.reduce((n, r) => n + arquivosDoRecebimento(r), 0);
  const fora = recibo.recebimentos.reduce((n, r) => n + r.nao_examinados.length, 0);
  return contagem(arquivos, fora);
}

/**
 * O recibo do material como o HTML 6.7 o mostra (E163.F2, GUS-209 A67-11): recebido em, operador,
 * os arquivos originais pelo nome, o SHA-256 (um por original) e o alcance demonstrado. O código do
 * caso e o inventário completo seguem nos artefatos e na consulta autorizada.
 */
export function Recibo({ recibo }: { recibo: ReciboDaCustodiaData }): JSX.Element {
  const operadores = operadoresDoRecibo(recibo);
  const linhas: [string, ReactNode, string][] = [
    [
      t.recebidoEm,
      recibo.recebido_em !== null ? formatarDataHora(recibo.recebido_em) : t.naoRegistrado,
      'custodia-recibo-recebido-em',
    ],
    [
      t.operador,
      operadores.length > 0 ? operadores.join(', ') : t.naoRegistrado,
      'custodia-recibo-operador',
    ],
    [
      t.originais,
      recibo.originais.length > 0 ? (
        <ul>
          {recibo.originais.map((o) => (
            <li key={o.documento_id}>{o.nome}</li>
          ))}
        </ul>
      ) : (
        t.naoRegistrado
      ),
      'custodia-recibo-originais',
    ],
    [t.hash, t.hashUmPorOriginal, 'custodia-recibo-hash'],
    [t.alcance, t.alcanceSelo, 'custodia-recibo-alcance'],
  ];
  return (
    <dl className="w-full" data-testid="custodia-recibo">
      {linhas.map(([rotulo, valor, testId]) => (
        <div
          key={rotulo}
          data-testid={testId}
          className="grid grid-cols-1 gap-1 border-b border-border py-2.5 text-[12px] last:border-b-0 sm:grid-cols-[160px_minmax(0,1fr)] sm:gap-[18px]"
        >
          <dt className="text-fg-muted">{rotulo}</dt>
          <dd className="m-0 font-semibold text-fg">{valor}</dd>
        </div>
      ))}
    </dl>
  );
}

export function LinhaNaoExaminadoDoRecibo({
  item,
}: {
  item: NaoExaminadoNoReciboData;
}): JSX.Element {
  return (
    <li
      data-testid="custodia-nao-examinado"
      className="flex flex-wrap items-center justify-between gap-sc-2 py-1"
    >
      <span className="min-w-0">
        <span className="block truncate text-body-sm text-fg">{item.nome}</span>
        <span className="text-caption text-fg-muted">
          {t.naoExaminadoLinha.replace('{motivo}', textoDoMotivo(item.motivo))}
        </span>
      </span>
      <span className="flex gap-sc-2">
        <SeloDaSituacao conferencia={item.conferencia} />
        <Badge variant="neutral">{t.seloNaoExaminado}</Badge>
      </span>
    </li>
  );
}

function tituloDoRecebimento(recebimento: RecebimentoNoReciboData): string {
  return recebimento.numero === null
    ? t.recebimentoSemRegistro
    : t.recebimentoNumero.replace('{n}', String(recebimento.numero));
}

function LinhaDoRecebimento({
  client,
  caseId,
  recebimento,
}: {
  client: AxiosInstance;
  caseId: string;
  recebimento: RecebimentoNoReciboData;
}): JSX.Element {
  const id = recebimento.recebimento_id;
  return (
    <li data-testid="custodia-recebimento" className="py-sc-2">
      <div className="flex flex-wrap items-center justify-between gap-sc-2">
        <div className="min-w-0">
          <p className="text-body-sm font-semibold text-fg">{tituloDoRecebimento(recebimento)}</p>
          <p className="text-caption text-fg-muted">
            {formatarDataHora(recebimento.recebido_em)}
            {recebimento.recebido_por !== null ? ` · ${recebimento.recebido_por}` : null}
            {id !== null ? (
              <>
                {' · '}
                <span className="font-mono" title={id}>
                  {t.recebimentoId.replace('{id}', idCurto(id))}
                </span>
              </>
            ) : null}
          </p>
        </div>
        <span className="text-caption text-fg-muted">
          {contagem(arquivosDoRecebimento(recebimento), recebimento.nao_examinados.length)}
        </span>
      </div>
      {recebimento.nao_examinados.length > 0 ? (
        <ul className="ml-sc-4 border-l border-border pl-sc-4">
          {recebimento.nao_examinados.map((item) => (
            <LinhaNaoExaminadoDoRecibo key={item.item_id} item={item} />
          ))}
        </ul>
      ) : null}
      <ManifestoDoRecebimento client={client} caseId={caseId} recebimento={recebimento} />
    </li>
  );
}

export function Recebimentos({
  client,
  caseId,
  recibo,
}: {
  client: AxiosInstance;
  caseId: string;
  recibo: ReciboDaCustodiaData;
}): JSX.Element | null {
  if (recibo.recebimentos.length === 0) return null;
  return (
    <CartaoDaAbertura titulo={t.recebimentosTitulo} testId="custodia-recebimentos">
      <ul className="divide-y divide-border">
        {recibo.recebimentos.map((recebimento) => (
          <LinhaDoRecebimento
            key={recebimento.recebimento_id ?? 'sem-registro'}
            client={client}
            caseId={caseId}
            recebimento={recebimento}
          />
        ))}
      </ul>
      <ComoConferir />
    </CartaoDaAbertura>
  );
}
